import type {
  BlockItem,
  ContentBlock,
  MediaRef,
  RichTextDocument,
  MediaType,
  TemplateChrome,
  TemplateLayout,
  RibbonAvailability,
  RibbonEnding,
  RibbonOpening,
  RibbonPlan,
} from "@diality/contracts";
import {
  hydratePageRibbon,
  hydrateRibbonAvailability,
  planRibbon,
  entryHeader,
  resolveSlotSchema,
  type CollectionDisplay,
  type EntryFields,
  type EntryHeaderData,
  type EntrySummary,
} from "@diality/contracts";
import {
  collectDataRequests,
  requestKey,
  EMPTY_BLOCK_DATA,
  type BlockData,
  type CollectionMeta,
} from "@diality/ui";
import type { LoaderFunctionArgs } from "react-router";
import { apiUrl, resolveApiEndpoint } from "./api-endpoint";
// The same ref→URL function the blocks render through. An entry's masthead
// picture is stored exactly like a block's, so resolving it any other way here
// would be a second, drifting copy of the crop-URL rules.
import { resolveMarketingMedia } from "./block-render";

export interface PageSummary {
  id: string;
  title: string;
  url: string;
  status: string;
  liveVersionId: string | null;
  draftVersionId: string | null;
}

/** A page's SEO metadata, as the public live detail carries it. */
export interface PageSeo {
  pageId: string;
  locale: string;
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: MediaRef | null;
}

interface PageDetail {
  page: {
    id: string;
    title: string;
    url: string;
    status: string;
  };
  /**
   * The LIVE version's ribbon: its switch and pins. Versioned with the blocks,
   * so a switch an author is still trying out never reaches a visitor before
   * Publish. Absent on older APIs, which reads as off.
   */
  ribbon?: {
    enabled?: boolean;
    opening?: RibbonOpening | null;
    ending?: RibbonEnding | null;
  } | null;
  /**
   * How to draw the page. Optional on the wire: a response from an API that
   * predates template presentation carries none, and such a page falls back to
   * DEFAULT_PAGE_TEMPLATE rather than losing its chrome.
   */
  template?: Partial<PageTemplate>;
  /** The collection this page is an entry of, and its display mapping. */
  collection?: {
    id: string;
    basePath: string;
    display: CollectionDisplay;
  } | null;
  /** An entry's live field values. */
  fields?: EntryFields;
  pointer: {
    liveVersionId: string | null;
    draftVersionId: string | null;
  };
  publishedAt?: string | null;
  /** The page's SEO metadata (unversioned — reflects edits immediately). */
  seo?: PageSeo | null;
  blocks: Array<{
    blockId: string;
    componentId: string;
    scalars?: Record<string, string>;
    documents?: Record<string, RichTextDocument>;
    media?: Record<string, { assetId?: string; cropId: string } | null>;
    repeats?: Record<
      string,
      Array<{
        itemId?: string;
        scalars?: Record<string, string>;
        documents?: Record<string, RichTextDocument>;
        media?: Record<string, { assetId?: string; cropId: string } | null>;
      }>
    >;
  }>;
}

/**
 * How a page is drawn — its template's layout and site chrome.
 *
 * Resolved by the API from the page's template at READ time, never snapshotted
 * onto the published version: an admin restyling a template changes every page
 * using it at once, and switching a page's template reaches a visitor without a
 * republish.
 */
export interface PageTemplate {
  layout: TemplateLayout;
  chrome: TemplateChrome;
  /** Whether pages of this template may show the ribbon. */
  ribbon: RibbonAvailability;
}

/**
 * The ribbon a page actually shows: its opening, ending and waypoints, resolved
 * from the template's availability, the page's own switch and pins, and the
 * roles its blocks imply. Plain data — the loader's output has to serialize, so
 * the composition itself is built on the client (see RibbonScene). Null when
 * the template says off or the page keeps it off.
 */
export type PageRibbon = RibbonPlan;

/** What the layout route resolves for the shell: chrome plus the ribbon. */
export interface PagePresentation {
  chrome: TemplateChrome;
  ribbon: PageRibbon | null;
  /** Stable identity of the published CMS page at this URL. */
  pageId: string | null;
}

export function resolvePageRibbon(
  detail: PageDetail,
  blocks: ContentBlock[],
): PageRibbon | null {
  const availability = hydrateRibbonAvailability(detail.template?.ribbon);
  const ribbon = hydratePageRibbon(detail.ribbon);
  if (availability === "off" || !ribbon.enabled) return null;
  return planRibbon(blocks, {
    opening: ribbon.opening,
    ending: ribbon.ending,
  });
}

/**
 * What a page is drawn as when the API says nothing.
 *
 * The site's own behaviour before templates could change it, so a response from
 * an older API — or a page whose template could not be read — renders exactly
 * as it always did rather than losing its nav and footer.
 */
export const DEFAULT_PAGE_TEMPLATE: PageTemplate = {
  layout: "full-width",
  chrome: { header: true, footer: true },
  ribbon: "optional",
};

const DEFAULT_PRESENTATION: PagePresentation = {
  chrome: DEFAULT_PAGE_TEMPLATE.chrome,
  ribbon: null,
  pageId: null,
};

export type MarketingPageLoaderData =
  | {
      kind: "cms";
      /** What the page's blocks asked for — entries they list but do not own. */
      blockData: BlockData;
      page: PageDetail["page"];
      pointer: PageDetail["pointer"];
      /** How to draw the page — see PageTemplate. */
      template: PageTemplate;
      /** The ribbon this page shows, resolved — see PageRibbon. */
      ribbon: PageRibbon | null;
      /**
       * The masthead an ARTICLE layout draws, when this page is an entry.
       *
       * Built here rather than in the renderer because it is the LOADER that
       * knows the collection's display mapping — which field is the date, which
       * is the summary. A layout that had to work that out would need the
       * collection, which would be a second request per article.
       */
      entry: EntryHeaderData | null;
      /** Where an article's way back leads — its collection's listing page. */
      entryBackHref: string | null;
      /** When the live version was published (null when never). */
      publishedAt: string | null;
      /** The page's SEO metadata (drives the head's title/description/OG). */
      seo: PageSeo | null;
      blocks: ContentBlock[];
    }
  | {
      kind: "static";
      reason: "api-unavailable" | "page-not-found" | "page-not-live";
    };

function normalizePath(pathname: string): string {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
}

/** Suffix React Router gives a client navigation's data request. */
const DATA_SUFFIX = ".data";

/**
 * The site path a request is for.
 *
 * A document request addresses the path itself, but React Router addresses a
 * client navigation's data request as `<path>.data` — `/_.data` where the route
 * is an index. Matching a CMS page on the raw pathname therefore found nothing
 * on every client navigation, so each CMS-composed route fell back to its static
 * component and the page a visitor saw depended on how they arrived.
 */
function requestPath(url: string): string {
  const { pathname } = new URL(url);
  if (!pathname.endsWith(DATA_SUFFIX)) return normalizePath(pathname);
  const base = pathname.slice(0, -DATA_SUFFIX.length);
  // An index route is addressed as `_`: /_.data → /, /news/_.data → /news.
  return normalizePath(base.replace(/(^|\/)_$/, "") || "/");
}

function toMedia(
  raw:
    | Record<
        string,
        { assetId?: string; cropId: string; mediaType?: MediaType } | null
      >
    | undefined,
  slotKeys: string[],
): Record<string, MediaRef | null> {
  const media: Record<string, MediaRef | null> = {};
  for (const key of slotKeys) media[key] = null; // declared slots default to unfilled
  for (const [key, ref] of Object.entries(raw ?? {})) {
    // Both halves, always. Narrowing to `{ cropId }` here would drop the asset
    // the ref names and leave the resolver unable to build a crop URL.
    media[key] = ref
      ? {
          assetId: ref.assetId,
          cropId: ref.cropId,
          // Carried through, not re-derived: the reference is what says a slot
          // holds a video, and a resolver that lost it would render a picture.
          ...(ref.mediaType ? { mediaType: ref.mediaType } : {}),
        }
      : null;
  }
  return media;
}

export function toContentBlocks(detail: PageDetail): ContentBlock[] {
  const versionId =
    detail.pointer.liveVersionId ??
    detail.pointer.draftVersionId ??
    `${detail.page.id}-unknown`;

  return detail.blocks.map((block, position) => {
    const schema = resolveSlotSchema(block.componentId);
    const cb: ContentBlock = {
      id: block.blockId,
      versionId,
      pageId: detail.page.id,
      locale: "en",
      componentId: block.componentId,
      position,
      scalars: block.scalars ?? {},
      media: toMedia(
        block.media,
        (schema.mediaSlots ?? []).map((s) => s.key),
      ),
    };
    if (block.documents) cb.documents = block.documents;

    const repeats: Record<string, BlockItem[]> = {};
    for (const [key, items] of Object.entries(block.repeats ?? {})) {
      const innerSchema = (schema.repeatSlots ?? []).find((s) => s.key === key);
      const innerImageKeys = (innerSchema?.mediaSlots ?? []).map((s) => s.key);
      repeats[key] = items.map((it) => {
        const item: BlockItem = {
          // Section/resource relationships use the API item's stable identity.
          id: it.itemId,
          scalars: it.scalars ?? {},
          media: toMedia(it.media, innerImageKeys),
        };
        if (it.documents) item.documents = it.documents;
        return item;
      });
    }
    if (Object.keys(repeats).length > 0) cb.repeats = repeats;

    return cb;
  });
}

/** Every active page, or null when the API can't be reached. */
/**
 * Per-request memo for the two reads every marketing request makes.
 *
 * The layout route and the leaf route both need the page behind this URL — the
 * leaf to render it, the layout to know whether the template wants the site
 * header and footer — and React Router runs their loaders in parallel against
 * the SAME Request object. Without this they would each fetch the page list and
 * the live version, doubling every marketing request's API traffic to answer
 * one question twice.
 *
 * Keyed on the Request and held weakly, so an entry lives exactly as long as
 * the request it belongs to. Promises are cached, not results, so a parallel
 * second caller waits on the first fetch rather than starting its own.
 */
const pageListByRequest = new WeakMap<
  Request,
  Promise<PageSummary[] | null>
>();
const liveDetailByRequest = new WeakMap<
  Request,
  Map<string, Promise<PageDetail | null>>
>();

export async function fetchPageList(
  endpoint: string,
  request: Request,
): Promise<PageSummary[] | null> {
  const cached = pageListByRequest.get(request);
  if (cached) return cached;
  const pending = fetchPageListUncached(endpoint, request);
  pageListByRequest.set(request, pending);
  return pending;
}

async function fetchPageListUncached(
  endpoint: string,
  request: Request,
): Promise<PageSummary[] | null> {
  const response = await fetch(apiUrl(endpoint, "/api/pages"), {
    signal: request.signal,
  });
  if (!response.ok) return null;
  const body = (await response.json()) as { pages?: PageSummary[] };
  return body.pages ?? [];
}

/** A page's published (live) version, or null when it can't be read. */
function fetchLiveDetail(
  endpoint: string,
  request: Request,
  pageId: string,
): Promise<PageDetail | null> {
  let byPage = liveDetailByRequest.get(request);
  if (!byPage) {
    byPage = new Map();
    liveDetailByRequest.set(request, byPage);
  }
  const cached = byPage.get(pageId);
  if (cached) return cached;
  const pending = fetchLiveDetailUncached(endpoint, request, pageId);
  byPage.set(pageId, pending);
  return pending;
}

async function fetchLiveDetailUncached(
  endpoint: string,
  request: Request,
  pageId: string,
): Promise<PageDetail | null> {
  const response = await fetch(
    apiUrl(endpoint, `/api/pages/${encodeURIComponent(pageId)}/live`),
    { signal: request.signal },
  );
  if (!response.ok) return null;
  return (await response.json()) as PageDetail;
}

/** The CMS-composed page for this request's path, if one is published. */
async function composeCmsPage(
  endpoint: string,
  request: Request,
  pages: PageSummary[],
): Promise<MarketingPageLoaderData> {
  const path = requestPath(request.url);
  const page = pages.find((candidate) => normalizePath(candidate.url) === path);
  if (!page) return { kind: "static", reason: "page-not-found" };
  if (!page.liveVersionId) return { kind: "static", reason: "page-not-live" };

  const detail = await fetchLiveDetail(endpoint, request, page.id);
  if (!detail) return { kind: "static", reason: "api-unavailable" };

  const blocks = toContentBlocks(detail);
  const entryFields = detail.fields;
  const entryCollection = detail.collection ?? null;
  return {
    kind: "cms",
    entry:
      entryCollection && entryFields
        ? entryHeader(
            { display: entryCollection.display },
            { title: detail.page.title, fields: entryFields },
            resolveMarketingMedia,
          )
        : null,
    entryBackHref: entryCollection?.basePath ?? null,
    page: detail.page,
    pointer: detail.pointer,
    blockData: await fetchBlockData(endpoint, request, blocks),
    template: {
      layout: detail.template?.layout ?? DEFAULT_PAGE_TEMPLATE.layout,
      chrome: detail.template?.chrome ?? DEFAULT_PAGE_TEMPLATE.chrome,
      ribbon: hydrateRibbonAvailability(detail.template?.ribbon),
    },
    ribbon: resolvePageRibbon(detail, blocks),
    publishedAt: detail.publishedAt ?? null,
    seo: detail.seo ?? null,
    blocks,
  };
}

/**
 * Fetch what the page's blocks asked for, before rendering.
 *
 * SERVER-SIDE and in PARALLEL. A Collection List that fetched for itself would
 * fetch after hydration, so a visitor would get an empty list that filled in a
 * moment later and a crawler would get an empty list full stop — which is the
 * whole reason the news page exists.
 *
 * A failed request is recorded as `error` and NEVER throws. The list says it
 * could not load; the rest of the page renders. A page that 500s because one
 * band could not reach its data would be a far worse failure than a band that
 * says so.
 */
async function fetchBlockData(
  endpoint: string,
  request: Request,
  blocks: ContentBlock[],
): Promise<BlockData> {
  const requests = collectDataRequests(blocks);
  if (requests.length === 0) return EMPTY_BLOCK_DATA;

  const data: BlockData = {
    collections: {},
    entries: {},
    status: {},
    nextOffsets: {},
  };

  await Promise.all(
    requests.map(async (dataRequest) => {
      const key = requestKey(dataRequest);
      try {
        const params = new URLSearchParams();
        if (dataRequest.sort) params.set("sort", dataRequest.sort);
        if (dataRequest.dir) params.set("dir", dataRequest.dir);
        if (dataRequest.limit) params.set("limit", String(dataRequest.limit));
        for (const f of dataRequest.filters ?? []) {
          params.append("filter", `${f.field}:${f.op}:${f.value}`);
        }
        const response = await fetch(
          apiUrl(
            endpoint,
            `/api/collections/${encodeURIComponent(dataRequest.collectionId)}/entries?${params}`,
          ),
          { signal: request.signal },
        );
        if (!response.ok) {
          // A collection that no longer exists is not an error the page should
          // shout about — the block names a stale id, which is a normal
          // consequence of deleting a collection. An empty, READY list is the
          // honest rendering; anything else is a failure to reach the data.
          data.status[key] = response.status === 404 ? "ready" : "error";
          data.entries[key] = [];
          return;
        }
        const body = (await response.json()) as {
          collection: CollectionMeta;
          entries: EntrySummary[];
          nextOffset?: number | null;
        };
        data.collections[dataRequest.collectionId] = body.collection;
        data.entries[key] = body.entries ?? [];
        data.nextOffsets![key] = body.nextOffset ?? null;
        data.status[key] = "ready";
      } catch {
        data.entries[key] = [];
        data.status[key] = "error";
      }
    }),
  );

  return data;
}

/**
 * Public marketing page loader.
 *
 * This is intentionally tolerant: the static page components remain the
 * fallback while DynamoDB-backed page content is still being filled out.
 */
export async function marketingPageLoader({
  request,
}: LoaderFunctionArgs): Promise<MarketingPageLoaderData> {
  try {
    const endpoint = await resolveApiEndpoint({
      baseUrl: request.url,
      signal: request.signal,
    });
    const pages = await fetchPageList(endpoint, request);
    if (!pages) return { kind: "static", reason: "api-unavailable" };
    return await composeCmsPage(endpoint, request, pages);
  } catch {
    return { kind: "static", reason: "api-unavailable" };
  }
}

/**
 * The site chrome AND background ribbon the page at this URL asks for.
 *
 * Resolved in the LAYOUT route's loader rather than read out of the leaf's,
 * because the header and footer are the layout's to render and React Router
 * gives a parent no view of a child's loader data — `useMatches()` reports
 * `data: null` for every match in this build, which is what a first attempt
 * discovered the expensive way.
 *
 * Costs nothing extra: both reads are memoised per Request, so this shares the
 * page list and live version the leaf loader fetches anyway.
 *
 * Falls back to showing both on ANY doubt — a static route, an unpublished
 * page, an unreachable API. A missing template must never cost a visitor the
 * site navigation.
 */
export async function pagePresentationLoader({
  request,
}: LoaderFunctionArgs): Promise<PagePresentation> {
  try {
    const endpoint = await resolveApiEndpoint({
      baseUrl: request.url,
      signal: request.signal,
    });
    const pages = await fetchPageList(endpoint, request);
    if (!pages) return DEFAULT_PRESENTATION;
    const path = requestPath(request.url);
    const page = pages.find(
      (candidate) => normalizePath(candidate.url) === path,
    );
    if (!page?.liveVersionId) return DEFAULT_PRESENTATION;
    const detail = await fetchLiveDetail(endpoint, request, page.id);
    if (!detail) return DEFAULT_PRESENTATION;
    return {
      chrome: detail.template?.chrome ?? DEFAULT_PAGE_TEMPLATE.chrome,
      pageId: detail.page.id,
      // The ribbon needs the blocks (its waypoints live on them); the detail is
      // memoised per Request so this is the same fetch the leaf loader makes.
      ribbon: resolvePageRibbon(detail, toContentBlocks(detail)),
    };
  } catch {
    return DEFAULT_PRESENTATION;
  }
}

/** @deprecated kept for callers that only want the chrome; prefer pagePresentationLoader. */
export async function pageChromeLoader(
  args: LoaderFunctionArgs,
): Promise<TemplateChrome> {
  return (await pagePresentationLoader(args)).chrome;
}
